///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { LinkFeature } from '../../../entities/link/link-feature';
import { GtthPreloaderModalEvent } from '../../../../../shared/preloader-modal/preloader-modal-event';
import { GtthLinkFeatureDtoModel } from '../../../../../services/project-socket/dto/gtth-link-feature-dto.model';
import { TopologyLinkTypeEnum } from '../../../../../services/project-socket/dto/topology-types.model';

export class GtthChangeTypeForSelectedLiksCommand extends GtthAbstractCommand {
    private oldModifyLinksList: GtthLinkFeatureDtoModel[];
    private linkIds: number[];
    private newLinkTypes;

    getCommandName(): string {
        return 'GtthChangeTypeForSelectedLiksCommand';
    }

    create(): this {
        this.linkIds = [];
        this.oldModifyLinksList = [];

        for (const link of this.historyService.logicalTopology.getSelectedLinks()) {
            if (!link.isUserForced()) {
                this.linkIds.push(link.getId());
                this.oldModifyLinksList.push(link.toGeoJson());
            }
        }

        return this;
    }

    setLinkType(newLinksType: TopologyLinkTypeEnum, usedProd0: number, usedProd1: number, usedAnt0: number, usedAnt1: number) {
        this.newLinkTypes = {
            link_type: newLinksType,
            used_prod_0: usedProd0,
            used_prod_1: usedProd1,
            used_ant0: usedAnt0,
            used_ant1: usedAnt1
        };

        this.linkIds.forEach((linkId: number) => {
            const link = this.historyService.logicalTopology.getLink(linkId);
            this.setLinkTypeParams(link, newLinksType, usedProd0, usedProd1, usedAnt0, usedAnt1);
        });
    }

    doCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const activity = this.createActivityDTO(false);
        activity.links = this.linkIds.map((linkId) => {
            const linkGeoJson = this.historyService.logicalTopology.getLink(linkId).toGeoJson();
            if (this.newLinkTypes) {
                linkGeoJson.setLinkType(this.newLinkTypes.link_type);
                linkGeoJson.setUsedProd0(this.newLinkTypes.used_prod_0);
                linkGeoJson.setUsedProd1(this.newLinkTypes.used_prod_1);
                linkGeoJson.setUsedAnt0(this.newLinkTypes.used_ant0);
                linkGeoJson.setUsedAnt1(this.newLinkTypes.used_ant1);
            }
            return linkGeoJson;
        });
        this.sendActivity(activity);
    }

    undoCommand(): void {
        this.historyService.eventManager.broadcast({
            name: 'togglePreloaderModal',
            eventObject: GtthPreloaderModalEvent.show('Loading')
        });

        const activity = this.createActivityDTO(false);
        activity.links = [...this.oldModifyLinksList];
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });

            this.historyService.notificationService.showError(activityDto?.error?.message);

            console.log('Command failed:', activityDto.error);

            if (this.oldModifyLinksList) {
                this.oldModifyLinksList.forEach((oldLink: GtthLinkFeatureDtoModel) => {
                    const link = this.historyService.logicalTopology.getLink(oldLink.getId());
                    link.setLinkType(oldLink.getLinkType());
                    link.setUsedProd0(oldLink.getUsedProd0());
                    link.setUsedProd1(oldLink.getUsedProd1());
                    link.setUsedAnt0(oldLink.getUsedAnt0());
                    link.setUsedAnt1(oldLink.getUsedAnt1());
                    this.setLinkTypeParams(
                        link,
                        oldLink.getLinkType(),
                        oldLink.getUsedProd0(),
                        oldLink.getUsedProd1(),
                        oldLink.getUsedAnt0(),
                        oldLink.getUsedAnt1()
                    );
                });
            }
        } else {
            activityDto.links.forEach((activityLink: GtthLinkFeatureDtoModel) => {
                const link = this.historyService.logicalTopology.getLink(activityLink.getId());
                this.setLinkTypeParams(
                    link,
                    activityLink.getLinkType(),
                    activityLink.getUsedProd0(),
                    activityLink.getUsedProd1(),
                    activityLink.getUsedAnt0(),
                    activityLink.getUsedAnt1()
                );
            });

            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });
        }

        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });

            this.historyService.notificationService.showError(activityDto?.error?.message);

            console.log('Command failed:', activityDto.error);
        } else {
            activityDto.links.forEach((activityLink: GtthLinkFeatureDtoModel) => {
                const link = this.historyService.logicalTopology.getLink(activityLink.getId());
                this.setLinkTypeParams(
                    link,
                    activityLink.getLinkType(),
                    activityLink.getUsedProd0(),
                    activityLink.getUsedProd1(),
                    activityLink.getUsedAnt0(),
                    activityLink.getUsedAnt1()
                );
            });

            this.historyService.eventManager.broadcast({
                name: 'togglePreloaderModal',
                eventObject: GtthPreloaderModalEvent.hide()
            });
        }

        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isModifyTopology(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return true;
    }

    private setLinkTypeParams(
        link: LinkFeature,
        newLinksType: TopologyLinkTypeEnum,
        usedProd0: number,
        usedProd1: number,
        usedAnt0: number,
        usedAnt1: number
    ) {
        if (
            link.getLinkType() !== newLinksType ||
            link.getUsedProd0() !== usedProd0 ||
            link.getUsedProd1() !== usedProd1 ||
            link.getUsedAnt0() !== usedAnt0 ||
            link.getUsedAnt1() !== usedAnt1
        ) {
            link.setLinkType(newLinksType);
            link.setUsedProd0(usedProd0);
            link.setUsedProd1(usedProd1);
            link.setUsedAnt0(usedAnt0);
            link.setUsedAnt1(usedAnt1);
        }
    }
}
